import React, { useEffect, useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { adminOverview, adminUsers, adminExportUrl, adminGetSettings, adminPutSettings, adminListDiscounts, adminCreateDiscount, adminLogin } from "@/lib/admin";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";
const PERIODS = [{ k: "day", l: "Jour" }, { k: "week", l: "7 jours" }, { k: "month", l: "30 jours" }];
const NAME_ALIASES = { "united states": "united states of america", "russia": "russian federation", "czechia": "czech republic" };
const norm = (s) => (s || "").toLowerCase().trim();

const Stat = ({ label, value, suffix = "" }) => (
    <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 12 }}>
        <div style={{ fontSize: 11, color: "var(--text-secondary)" }}>{label}</div>
        <div className="font-display font-black text-white" style={{ fontSize: 24, lineHeight: 1.1 }}>{value ?? 0}{suffix}</div>
    </div>
);
const Bar = ({ label, count, max }) => (
    <div className="flex items-center gap-2 py-1">
        <span className="text-xs text-white/85 w-32 shrink-0 truncate">{label}</span>
        <div className="flex-1 h-4 rounded overflow-hidden" style={{ background: "var(--surface-2)" }}>
            <div className="h-full bg-revred" style={{ width: `${max ? (count / max) * 100 : 0}%` }} />
        </div>
        <span className="text-xs text-white w-10 text-right">{count}</span>
    </div>
);

const SETTINGS_FIELDS = [
    ["price_monthly", "Prix mensuel (€)"], ["price_yearly", "Prix annuel (€)"], ["price_offer", "Prix offre de lancement (€)"],
    ["countdown_minutes", "Durée du compte à rebours (min)"],
    ["pack_small_credits", "Petit pack — crédits"], ["pack_small_price", "Petit pack — prix (€)"],
    ["pack_big_credits", "Grand pack — crédits"], ["pack_big_price", "Grand pack — prix (€)"],
    ["credit_unit_price", "Tarif unitaire crédit — champ libre (€)"],
    ["coach_msg_cost", "Coût d'un message (crédits)"], ["plan_cost", "Coût d'un plan (crédits)"],
    ["monthly_analyses", "Analyses incluses / mois"], ["monthly_credits", "Crédits inclus / mois"],
    ["referral_bonus", "Bonus de parrainage (crédits)"],
];

const WorldMap = ({ countries }) => {
    const byName = useMemo(() => {
        const m = {};
        (countries || []).forEach((c) => { m[norm(c.country)] = c; });
        return m;
    }, [countries]);
    const max = Math.max(...(countries || []).map((c) => c.users), 1);
    const lookup = (geoName) => {
        const n = norm(geoName);
        return byName[n] || byName[NAME_ALIASES[n]] || null;
    };
    return (
        <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }} data-testid="admin-worldmap">
            <ComposableMap projectionConfig={{ scale: 130 }} height={360} style={{ width: "100%", height: "auto" }}>
                <Geographies geography={GEO_URL}>
                    {({ geographies }) => geographies.map((geo) => {
                        const c = lookup(geo.properties.name);
                        const intensity = c ? 0.2 + 0.8 * (c.users / max) : 0;
                        return (
                            <Geography key={geo.rsmKey} geography={geo}
                                fill={c ? `rgba(225,6,0,${intensity})` : "#1a1a1a"}
                                stroke="#000" strokeWidth={0.4}
                                style={{ default: { outline: "none" }, hover: { outline: "none", fill: "var(--red)" }, pressed: { outline: "none" } }} />
                        );
                    })}
                </Geographies>
            </ComposableMap>
        </div>
    );
};

const Admin = () => {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [period, setPeriod] = useState("week");
    const [users, setUsers] = useState([]);
    const [q, setQ] = useState("");
    const [tab, setTab] = useState("dash"); // dash | settings | codes
    const [settings, setSettings] = useState(null);
    const [savedMsg, setSavedMsg] = useState("");
    const [discounts, setDiscounts] = useState([]);
    const [newCode, setNewCode] = useState({ code: "", percent: 10, end_date: "", max_uses: "" });
    const [codeMsg, setCodeMsg] = useState("");
    const [pwd, setPwd] = useState("");
    const [loginErr, setLoginErr] = useState("");

    const loadAll = () => {
        adminOverview().then(setData).catch((e) => setErr(e.response?.status || "err"));
        adminUsers().then(setUsers).catch(() => {});
        adminGetSettings().then(setSettings).catch(() => {});
        adminListDiscounts().then(setDiscounts).catch(() => {});
    };

    useEffect(() => { loadAll(); }, []);

    const doLogin = async () => {
        setLoginErr("");
        try { await adminLogin(pwd); setErr(null); setPwd(""); loadAll(); }
        catch (e) { setLoginErr("Mot de passe invalide."); }
    };

    const search = async (val) => { setQ(val); try { setUsers(await adminUsers(val)); } catch (e) {} };
    const saveSettings = async () => {
        try { const s = await adminPutSettings(settings); setSettings(s); setSavedMsg("Réglages enregistrés et répercutés dans Stripe."); setTimeout(() => setSavedMsg(""), 4000); }
        catch (e) { setSavedMsg("Erreur d'enregistrement."); }
    };
    const createCode = async () => {
        setCodeMsg("");
        try {
            const body = { code: newCode.code, percent: Number(newCode.percent), end_date: newCode.end_date || null, max_uses: newCode.max_uses ? Number(newCode.max_uses) : null };
            await adminCreateDiscount(body);
            setDiscounts(await adminListDiscounts());
            setNewCode({ code: "", percent: 10, end_date: "", max_uses: "" });
            setCodeMsg("Code créé.");
        } catch (e) { setCodeMsg(e.response?.data?.detail || "Erreur lors de la création."); }
    };

    if (err === 401 || err === 403) {
        return (
            <div className="fixed inset-0 bg-black flex flex-col items-center justify-center gap-4 px-6" data-testid="admin-login">
                <h2 className="font-display font-black uppercase text-white text-3xl">Back-office</h2>
                <p className="text-revtext-secondary text-sm">Entre le mot de passe administrateur.</p>
                <input data-testid="admin-password" type="password" value={pwd} autoComplete="current-password"
                    onChange={(e) => setPwd(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") doLogin(); }}
                    placeholder="Mot de passe" style={{ fontSize: 16, background: "var(--surface)", border: "1px solid var(--border)" }}
                    className="w-full max-w-[320px] rounded-xl px-4 h-12 text-white outline-none focus:border-revred" />
                {loginErr && <p className="text-sm text-revred" data-testid="admin-login-err">{loginErr}</p>}
                <button data-testid="admin-login-btn" onClick={doLogin}
                    className="tap-feedback w-full max-w-[320px] h-12 rounded-xl bg-revred text-white font-display font-black uppercase">Se connecter</button>
            </div>
        );
    }
    if (!data) return <div className="fixed inset-0 bg-black" />;

    const m = data[period] || {};
    const fmax = Math.max(...(data.funnel || []).map((f) => f.count), 1);

    return (
        <div className="fixed inset-0 bg-black max-w-[560px] mx-auto overflow-y-auto scrollbar-none px-5 py-6 safe-top safe-bottom" data-testid="admin-screen">
            <div className="flex items-center justify-between mb-4">
                <h1 className="font-display font-black uppercase text-white text-3xl">Admin</h1>
                <a data-testid="export-csv" href={adminExportUrl} className="text-xs text-revred underline">Export CSV</a>
            </div>

            <div className="flex gap-2 mb-5">
                {[["dash", "Tableau de bord"], ["settings", "Réglages"], ["codes", "Codes promo"]].map(([k, l]) => (
                    <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
                        className={`px-3 py-1.5 rounded-full text-sm border ${tab === k ? "border-revred text-white bg-revred/15" : "border-ink-700 text-revtext-secondary"}`}>{l}</button>
                ))}
            </div>

            {tab === "dash" && (
                <>
                    <div className="flex gap-2 mb-4">
                        {PERIODS.map((p) => (
                            <button key={p.k} data-testid={`period-${p.k}`} onClick={() => setPeriod(p.k)}
                                className={`px-3 py-1.5 rounded-full text-sm border ${period === p.k ? "border-revred text-white bg-revred/15" : "border-ink-700 text-revtext-secondary"}`}>{p.l}</button>
                        ))}
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                        <Stat label="Visiteurs" value={data.funnel?.[0]?.count} />
                        <Stat label="Questionnaires commencés" value={m.questionnaires_started} />
                        <Stat label="Questionnaires finis" value={m.questionnaires_done} />
                        <Stat label="Comptes créés" value={m.emails} />
                        <Stat label="Analyses générées" value={m.analyses} />
                        <Stat label="Abos actifs" value={(data.active_monthly || 0) + (data.active_yearly || 0)} />
                        <Stat label="Résiliations" value={m.cancellations} />
                        <Stat label="Revenu total" value={m.revenue_total} suffix=" €" />
                        <Stat label="Panier moyen" value={m.avg_cart} suffix=" €" />
                        <Stat label="MRR" value={data.mrr} suffix=" €" />
                        <Stat label="ARR" value={data.arr} suffix=" €" />
                        <Stat label="Coût IA" value={m.ai_cost_est} suffix=" €" />
                        <Stat label="Marge" value={m.margin} suffix=" €" />
                        <Stat label="Crédits consommés" value={m.credits_consumed} />
                        <Stat label="Offres express" value={m.offers_used} />
                    </div>

                    <h2 className="font-display font-black uppercase text-white text-lg mt-6 mb-2">Entonnoir de conversion</h2>
                    <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 12 }}>
                        {(data.funnel || []).map((f, i) => {
                            const prev = i > 0 ? data.funnel[i - 1].count : null;
                            const rate = prev ? Math.round((f.count / prev) * 100) : null;
                            return (
                                <div key={f.step}>
                                    <Bar label={f.step} count={f.count} max={fmax} />
                                    {rate != null && <div className="text-[10px] text-revtext-secondary text-right pr-12">{rate}%</div>}
                                </div>
                            );
                        })}
                    </div>

                    <h2 className="font-display font-black uppercase text-white text-lg mt-6 mb-2">Carte du monde</h2>
                    <WorldMap countries={data.countries} />
                    <div className="mt-3 flex flex-col gap-1" data-testid="admin-top-countries">
                        {(data.top_countries || []).length === 0 && <p className="text-sm text-revtext-secondary">Pas encore de données géographiques.</p>}
                        {(data.top_countries || []).map((c, i) => (
                            <div key={c.country_code} className="flex items-center justify-between text-sm py-1" style={{ borderBottom: "1px solid var(--border)" }}>
                                <span className="text-white/85">{i + 1}. {c.country || c.country_code}</span>
                                <span className="text-revtext-secondary">{c.users} users · {Number(c.revenue).toFixed(2).replace(".", ",")} €</span>
                            </div>
                        ))}
                    </div>

                    <h2 className="font-display font-black uppercase text-white text-lg mt-6 mb-2">Sources de trafic</h2>
                    <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 12 }} data-testid="admin-sources">
                        <div className="flex items-center text-[11px] text-revtext-secondary pb-2" style={{ borderBottom: "1px solid var(--border)" }}>
                            <span className="flex-1">Source</span><span className="w-16 text-right">Visiteurs</span><span className="w-14 text-right">Clients</span><span className="w-14 text-right">Conv.</span>
                        </div>
                        {(data.sources || []).map((s) => (
                            <div key={s.source} className="flex items-center text-sm py-1.5">
                                <span className="flex-1 text-white/85 truncate">{s.source}</span>
                                <span className="w-16 text-right text-white">{s.visitors}</span>
                                <span className="w-14 text-right text-white">{s.clients}</span>
                                <span className="w-14 text-right text-revred">{s.conversion}%</span>
                            </div>
                        ))}
                    </div>

                    <h2 className="font-display font-black uppercase text-white text-lg mt-6 mb-2">Utilisateurs</h2>
                    <input data-testid="user-search" value={q} onChange={(e) => search(e.target.value)}
                        placeholder="Rechercher par email" style={{ fontSize: "16px", background: "var(--surface)", border: "1px solid var(--border)" }}
                        className="w-full rounded-xl px-4 h-11 text-white outline-none focus:border-revred mb-3" />
                    <div className="flex flex-col gap-2 pb-4">
                        {users.map((u, i) => (
                            <div key={i} data-testid={`admin-user-${i}`} className="rounded-lg p-3" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                                <div className="flex items-center justify-between">
                                    <span className="text-white text-sm font-semibold truncate">{u.prenom || "—"} · {u.email}</span>
                                    <span className={`text-[11px] px-2 py-0.5 rounded ${u.sub_status === "active" ? "bg-revred text-white" : "bg-ink-700 text-revtext-secondary"}`}>{u.sub_status}</span>
                                </div>
                                <div className="text-[11px] text-revtext-secondary mt-1">
                                    {u.age ? `${u.age} ans · ` : ""}{u.created_at ? new Date(u.created_at).toLocaleDateString("fr-FR") : ""} · {u.credits} crédits · {u.analyses} analyses · {u.source}
                                </div>
                            </div>
                        ))}
                        {!users.length && <p className="text-sm text-revtext-secondary">Aucun utilisateur.</p>}
                    </div>
                </>
            )}

            {tab === "settings" && settings && (
                <div data-testid="admin-settings">
                    <p className="text-sm text-revtext-secondary mb-4">Toute modification s'applique immédiatement et se répercute dans Stripe.</p>
                    <div className="grid grid-cols-2 gap-3">
                        {SETTINGS_FIELDS.map(([key, label]) => (
                            <div key={key}>
                                <label className="block text-[11px] text-revtext-secondary mb-1">{label}</label>
                                <input data-testid={`setting-${key}`} type="number" step="0.01" value={settings[key]}
                                    onChange={(e) => setSettings((s) => ({ ...s, [key]: e.target.value === "" ? "" : Number(e.target.value) }))}
                                    style={{ fontSize: 16, background: "var(--surface)", border: "1px solid var(--border)" }}
                                    className="w-full rounded-xl px-3 h-11 text-white outline-none focus:border-revred" />
                            </div>
                        ))}
                    </div>
                    {savedMsg && <p className="text-sm text-revred mt-3" data-testid="settings-msg">{savedMsg}</p>}
                    <button data-testid="settings-save" onClick={saveSettings} className="tap-feedback w-full mt-4 h-12 rounded-xl bg-revred text-white font-display font-black uppercase">Enregistrer</button>
                </div>
            )}

            {tab === "codes" && (
                <div data-testid="admin-codes">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                            <label className="block text-[11px] text-revtext-secondary mb-1">Code</label>
                            <input data-testid="code-code" value={newCode.code} onChange={(e) => setNewCode((c) => ({ ...c, code: e.target.value.toUpperCase() }))}
                                style={{ fontSize: 16, background: "var(--surface)", border: "1px solid var(--border)" }} className="w-full rounded-xl px-3 h-11 text-white outline-none focus:border-revred" />
                        </div>
                        <div>
                            <label className="block text-[11px] text-revtext-secondary mb-1">Pourcentage</label>
                            <input data-testid="code-percent" type="number" value={newCode.percent} onChange={(e) => setNewCode((c) => ({ ...c, percent: e.target.value }))}
                                style={{ fontSize: 16, background: "var(--surface)", border: "1px solid var(--border)" }} className="w-full rounded-xl px-3 h-11 text-white outline-none focus:border-revred" />
                        </div>
                        <div>
                            <label className="block text-[11px] text-revtext-secondary mb-1">Usages max</label>
                            <input data-testid="code-max" type="number" value={newCode.max_uses} onChange={(e) => setNewCode((c) => ({ ...c, max_uses: e.target.value }))}
                                style={{ fontSize: 16, background: "var(--surface)", border: "1px solid var(--border)" }} className="w-full rounded-xl px-3 h-11 text-white outline-none focus:border-revred" />
                        </div>
                        <div className="col-span-2">
                            <label className="block text-[11px] text-revtext-secondary mb-1">Date de fin</label>
                            <input data-testid="code-end" type="date" value={newCode.end_date} onChange={(e) => setNewCode((c) => ({ ...c, end_date: e.target.value }))}
                                style={{ fontSize: 16, background: "var(--surface)", border: "1px solid var(--border)" }} className="w-full rounded-xl px-3 h-11 text-white outline-none focus:border-revred" />
                        </div>
                    </div>
                    {codeMsg && <p className="text-sm text-revred mt-3" data-testid="code-msg">{codeMsg}</p>}
                    <button data-testid="code-create" onClick={createCode} className="tap-feedback w-full mt-4 h-12 rounded-xl bg-revred text-white font-display font-black uppercase">Créer le code</button>

                    <h2 className="font-display font-black uppercase text-white text-lg mt-6 mb-2">Codes existants</h2>
                    <div className="flex flex-col gap-2">
                        {discounts.length === 0 && <p className="text-sm text-revtext-secondary">Aucun code.</p>}
                        {discounts.map((d) => (
                            <div key={d.code} className="rounded-lg p-3 flex items-center justify-between" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                                <span className="text-white text-sm font-semibold">{d.code} · -{d.percent}%</span>
                                <span className="text-[11px] text-revtext-secondary">{d.max_uses ? `${d.max_uses} max` : "illimité"}{d.end_date ? ` · jusqu'au ${new Date(d.end_date).toLocaleDateString("fr-FR")}` : ""}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Admin;
